import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import { validateCategory } from "../../../utils/validation";
import {
  fetchCategoryData,
  addCategory,
  updateCategory,
} from "../../../services/MasterSettings/categoryServices";
import allImages from "../../../assets/images-import";
import { handleErrors } from "../../../utils/errorHandler";
import { Loading } from "../../Common/OtherElements/Loading";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import { resolveFileUrl } from "../../../utils/fileUrlHelper";

const INITIAL_FORM_STATE = {
  CategoryName: "",
  CategorySlug: "",
  ShortDesc: "",
  FullDesc: "",
  MetaTitle: "",
  MetaDesc: "",
  MetaKeywords: "",
  CategoryImage: "",
  CategoryImagePreview: "",
};

export const AddCategory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [content, setContent] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);
  const [PageLevelAccessurl, setPageLevelAccessurl] = useState();

  const generateCategorySlug = (value) =>
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9 ]/g, "")
      .replace(/\s+/g, "-");

  useEffect(() => {
    if (id) {
      setPageLevelAccessurl("/category/update/:id");
    } else {
      setPageLevelAccessurl("category/add");
    }
  }, [id]);

  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);

  useEffect(() => {
    if (pageAccessData) {
      if (id) {
        if (!pageAccessData.editAccess) {
          navigate("/404-error-page");
        }
      } else {
        if (!pageAccessData.addAccess) {
          navigate("/404-error-page");
        }
      }
    } else {
      console.log("No page access details found");
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        setLoading(true);
        try {
          const data = await fetchCategoryData(id);
          if (data) {
            setFormData({
              CategoryName: data.categoryName || "",
              CategorySlug: data.categorySlug || "",
              ShortDesc: data.shortDesc || "",
              FullDesc: data.fullDesc || "",
              MetaTitle: data.metaTitle || "",
              MetaDesc: data.metaDesc || "",
              MetaKeywords: data.metaKeywords || "",
              CategoryImage: "",
              CategoryImagePreview: resolveFileUrl(data.categoryImage) || "",
            });
            setContent(data.fullDesc || "");
            setIsSlugManuallyEdited(true);
          }
        } catch (error) {
          handleErrors(error);
        } finally {
          setLoading(false);
        }
      } else {
        setFormData(INITIAL_FORM_STATE);
        setContent("");
        setIsSlugManuallyEdited(false);
      }
    };
    fetchData();
  }, [id]);

  const handleEditorChange = (value) => {
    setContent(value);
    setFormData((prevData) => ({ ...prevData, FullDesc: value }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "CategoryName") {
      setFormData((prevData) => ({
        ...prevData,
        CategoryName: value,
        CategorySlug: isSlugManuallyEdited
          ? prevData.CategorySlug
          : generateCategorySlug(value),
      }));
    } else if (name === "CategorySlug") {
      setIsSlugManuallyEdited(true);
      setFormData((prevData) => ({
        ...prevData,
        CategorySlug: generateCategorySlug(value),
      }));
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    }

    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prevData) => ({
        ...prevData,
        CategoryImage: file,
        CategoryImagePreview: URL.createObjectURL(file),
      }));
      setErrors((prevErrors) => ({ ...prevErrors, CategoryImage: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { valid, errors: validationErrors } = validateCategory(formData);
    setErrors(validationErrors);

    if (valid) {
      setLoading(true);
      setIsButtonDisabled(true);
      try {
        if (id) {
          await updateCategory({ ...formData, id });
          toast.success("Category updated successfully!");
        } else {
          await addCategory(formData);
          toast.success("Category added successfully!");
        }
        navigate("/category");
      } catch (error) {
        handleErrors(error);
      } finally {
        setLoading(false);
        setIsButtonDisabled(false);
      }
    }
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">{id ? "Update Category" : "Add Category"}</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item">
                  <Link to="/">
                    <i className="ri-home-2-fill"></i>
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/category">Manage Categories</Link>
                </li>
                <li className="breadcrumb-item">
                  {id ? `Update Category-${id}` : "Add Category"}
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="card-body p-2">
        <form onSubmit={handleSubmit} method="POST">
          <div className="row">
            <div className="col-lg-8">
              <div className="card mt-xxl-n5 p-3">
                <div className="card-header-wrapper p-1">
                  <h5 className="blogs-heading">Category Details</h5>
                </div>
                <div className="blog-name-wrapper mt-3">
                  <div className="mb-3 col-lg-6">
                    <label className="form-label">
                      Category Name <span className="required-field">*</span>
                    </label>
                    <input
                      type="text"
                      name="CategoryName"
                      value={formData.CategoryName}
                      placeholder="Enter Category Name"
                      onChange={handleInputChange}
                      className={`form-control ${
                        errors.CategoryName ? "is-invalid" : ""
                      }`}
                    />
                    {errors.CategoryName && (
                      <div className="invalid-feedback">
                        {errors.CategoryName}
                      </div>
                    )}
                  </div>

                  <div className="mb-3 col-lg-6">
                    <label className="form-label">
                      Category Slug <span className="required-field">*</span>
                    </label>
                    <input
                      type="text"
                      name="CategorySlug"
                      value={formData.CategorySlug}
                      placeholder="Enter Category Slug"
                      onChange={handleInputChange}
                      className={`form-control ${
                        errors.CategorySlug ? "is-invalid" : ""
                      }`}
                    />
                    {errors.CategorySlug && (
                      <div className="invalid-feedback">
                        {errors.CategorySlug}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Short Description</label>
                  <textarea
                    name="ShortDesc"
                    value={formData.ShortDesc}
                    placeholder="Enter Short Description"
                    onChange={handleInputChange}
                    className={`form-control ${
                      errors.ShortDesc ? "is-invalid" : ""
                    }`}
                    rows="3"
                  ></textarea>
                  {errors.ShortDesc && (
                    <div className="invalid-feedback">
                      {errors.ShortDesc}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">Full Description</label>
                  <Editor
                    tinymceScriptSrc="/tinymce/tinymce.min.js"
                    value={content}
                    init={{
                      height: 400,
                      menubar: false,
                      plugins: [
                        "advlist",
                        "autolink",
                        "link",
                        "image",
                        "lists",
                        "charmap",
                        "preview",
                        "anchor",
                        "pagebreak",
                        "searchreplace",
                        "wordcount",
                        "visualblocks",
                        "code",
                        "fullscreen",
                        "insertdatetime",
                        "media",
                        "table",
                        "emoticons",
                        "template",
                        "help",
                      ],
                      toolbar:
                        "undo redo | styles | bold italic | forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image | preview media fullscreen | emoticons | help",
                    }}
                    onEditorChange={handleEditorChange}
                  />
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="card mt-xxl-n5 p-3">
                <div className="card-header-wrapper">
                  <h5 className="">Category Image</h5>
                </div>
                <div className="mt-3 d-flex justify-content-center">
                  <div className="profile-user position-relative d-inline-block mx-auto mb-4">
                    <img
                      src={
                        formData.CategoryImagePreview
                          ? resolveFileUrl(formData.CategoryImagePreview)
                          : allImages.DefultImage
                      }
                      className="rounded-circle avatar-xl img-thumbnail user-profile-image shadow"
                      alt="Category Preview"
                    />
                    <div className="avatar-xs p-0 rounded-circle profile-photo-edit">
                      <input
                        id="categoryImage"
                        type="file"
                        accept="image/*"
                        className="profile-img-file-input"
                        onChange={handleImageChange}
                      />
                      <label
                        htmlFor="categoryImage"
                        className="profile-photo-edit avatar-xs"
                      >
                        <span className="avatar-title rounded-circle bg-light text-body shadow">
                          <i className="ri-camera-fill"></i>
                        </span>
                      </label>
                    </div>
                    {errors.CategoryImage && (
                      <div className="invalid-feedback d-block">
                        {errors.CategoryImage}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="card mt-xxl-n5 p-3">
                <div className="card-header-wrapper">
                  <h5 className="">Meta Data</h5>
                </div>
                <div className="mt-3">
                  <label className="form-label">Meta Title</label>
                  <input
                    type="text"
                    name="MetaTitle"
                    value={formData.MetaTitle}
                    placeholder="Enter Meta Title"
                    onChange={handleInputChange}
                    className="form-control"
                  />
                </div>
                <div className="mt-2">
                  <label className="form-label">Meta Keywords</label>
                  <input
                    type="text"
                    name="MetaKeywords"
                    value={formData.MetaKeywords}
                    placeholder="Enter Meta Keywords"
                    onChange={handleInputChange}
                    className="form-control"
                  />
                </div>
                <div className="mt-2">
                  <label className="form-label">Meta Description</label>
                  <textarea
                    name="MetaDesc"
                    value={formData.MetaDesc}
                    placeholder="Enter Meta Description"
                    onChange={handleInputChange}
                    className="form-control"
                    rows="3"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-12">
            <div className="pt-4">
              <button
                type="submit"
                className="btn btn-secondary"
                disabled={isButtonDisabled}
              >
                {isButtonDisabled
                  ? id
                    ? "Updating"
                    : "Saving"
                  : id
                  ? "Update"
                  : "Save"}
              </button>
              <Link to="/category" className="btn btn-danger ms-1">
                Cancel
              </Link>
            </div>
          </div>
          {loading && <Loading />}
        </form>
      </div>
    </>
  );
};